"use client";

import { useRouter } from "next/navigation";
import { SportBetaBadge } from "@/app/_components/conditions/SportBetaBadge";

export type BestTimeSportOption = {
  key: string;
  label: string;
  icon: string;
  group: "mountain" | "coastal";
  isBeta?: boolean;
};

export function BestTimeSportToggle({
  activeSport,
  sports,
}: {
  activeSport: string;
  sports: readonly BestTimeSportOption[];
}) {
  const router = useRouter();
  const mountainSports = sports.filter((sport) => sport.group === "mountain");
  const coastalSports = sports.filter((sport) => sport.group === "coastal");

  // Switching sport navigates to the exact server-validated route. Client-only
  // params are dropped by moving to the clean per-sport URL. `replace` keeps the
  // back button from stepping through every toggle.
  function handleClick(sport: string) {
    if (sport === activeSport) return;
    router.replace(`/best-time-to-go/${sport}`, { scroll: false });
  }

  return (
    <div className="space-y-3">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-[1.2px] text-white/40 mb-2">
          Mountain
        </div>
        <div className="flex flex-wrap gap-2">
          {mountainSports.map((sport) => (
            <SportButton
              key={sport.key}
              sport={sport}
              isActive={activeSport === sport.key}
              onClick={handleClick}
            />
          ))}
        </div>
      </div>
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-[1.2px] text-white/40 mb-2">
          Water & Coastal
        </div>
        <div className="flex flex-wrap gap-2">
          {coastalSports.map((sport) => (
            <SportButton
              key={sport.key}
              sport={sport}
              isActive={activeSport === sport.key}
              onClick={handleClick}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function SportButton({
  sport,
  isActive,
  onClick,
}: {
  sport: BestTimeSportOption;
  isActive: boolean;
  onClick: (sport: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onClick(sport.key)}
      className={`flex min-h-11 items-center gap-2 rounded-[10px] px-3 py-2 text-[13px] font-medium transition-all ${
        isActive
          ? "bg-gold text-white ring-2 ring-white/50 shadow-lg"
          : "bg-white/15 text-white/90 hover:bg-white/25"
      }`}
      aria-label={`View ${sport.label}${sport.isBeta ? " Beta" : ""} rankings`}
    >
      <span className="text-[15px]">{sport.icon}</span>
      <span className="font-medium">{sport.label}</span>
      {sport.isBeta && <SportBetaBadge emphasised />}
    </button>
  );
}
